/**
 * PrivilegesManagementPage — Administration Escales Privileges.
 * - Liste + filtres (statut, categorie, featured, recherche)
 * - Actions par ligne : editer, dupliquer, activer, suspendre, archiver, supprimer
 * - Bouton "Nouvelle offre" -> ouvre PrivilegeEditorDialog
 */
import { useEffect, useMemo, useState } from "react";
import {
  getAdminPrivileges, deleteAdminPrivilege, duplicateAdminPrivilege,
  activateAdminPrivilege, suspendAdminPrivilege, archiveAdminPrivilege,
  getMedia, getPartners,
} from "../lib/api";
import { getPublicThumbnail } from "../lib/mediaUrl";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, Plus, MoreVertical, Edit, Copy, Play, Pause, Archive, Trash2, Star, Search, Loader2,
} from "lucide-react";
import PrivilegeEditorDialog from "./PrivilegeEditorDialog";

const STATUS_META = {
  draft: { label: "Brouillon", cls: "bg-gray-100 text-gray-700 border-gray-200" },
  scheduled: { label: "Programmée", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  active: { label: "Active", cls: "bg-green-100 text-green-700 border-green-200" },
  ended: { label: "Terminée", cls: "bg-gray-100 text-gray-500 border-gray-200" },
  suspended: { label: "Suspendue", cls: "bg-orange-100 text-orange-700 border-orange-200" },
};

const CATEGORIES = [
  { value: "all", label: "Toutes catégories" },
  { value: "croisieres", label: "Croisières" },
  { value: "hotels", label: "Hôtels" },
  { value: "sejours", label: "Séjours" },
  { value: "excursions", label: "Excursions" },
  { value: "accessoires", label: "Accessoires" },
  { value: "bagagerie", label: "Bagagerie" },
  { value: "autre", label: "Autre" },
];

const STATUSES = [
  { value: "all", label: "Tous statuts" },
  { value: "draft", label: "Brouillon" },
  { value: "scheduled", label: "Programmée" },
  { value: "active", label: "Active" },
  { value: "ended", label: "Terminée" },
  { value: "suspended", label: "Suspendue" },
];

function useMediaThumb(mediaId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mediaId) { setUrl(null); return; }
    let alive = true;
    getMedia(mediaId).then((r) => alive && setUrl(getPublicThumbnail(r.data, 200))).catch(() => alive && setUrl(null));
    return () => { alive = false; };
  }, [mediaId]);
  return url;
}

function Thumbnail({ mediaId }) {
  const url = useMediaThumb(mediaId);
  if (!url) {
    return (
      <div className="w-12 h-9 rounded bg-secondary flex items-center justify-center flex-shrink-0">
        <Sparkles className="h-4 w-4 opacity-30" />
      </div>
    );
  }
  return <img src={url} alt="" className="w-12 h-9 rounded object-cover flex-shrink-0" />;
}

export default function PrivilegesManagementPage() {
  const [offers, setOffers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (featuredOnly) params.featured_on_homepage = true;
      if (search.trim()) params.q = search.trim();
      const [oRes, pRes] = await Promise.all([
        getAdminPrivileges(params),
        partners.length ? Promise.resolve({ data: partners }) : getPartners(),
      ]);
      setOffers(oRes.data || []);
      if (!partners.length) setPartners(pRes.data || []);
    } catch (err) {
      toast.error("Erreur de chargement des offres");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, categoryFilter, featuredOnly]);

  const partnersMap = useMemo(() => {
    const m = {};
    partners.forEach((p) => { m[p.id] = p; });
    return m;
  }, [partners]);

  const handleNew = () => { setEditingId(null); setEditorOpen(true); };
  const handleEdit = (id) => { setEditingId(id); setEditorOpen(true); };
  const handleEditorClose = (didSave) => {
    setEditorOpen(false);
    setEditingId(null);
    if (didSave) load();
  };

  const doAction = async (id, fn, successMsg) => {
    setBusyId(id);
    try {
      await fn(id);
      toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action impossible");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteAdminPrivilege(deleteTarget.id);
      toast.success("Offre supprimée");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="privileges-admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
            <Sparkles className="h-6 w-6" /> Escales Privilèges
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Offres partenaires réservées aux adhérents ITERCATUS.
          </p>
        </div>
        <Button onClick={handleNew} data-testid="privileges-new-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle offre
        </Button>
      </div>

      {/* Filtres */}
      <div className="bg-white border border-border rounded-lg p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (titre, accroche, description)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            className="pl-9"
            data-testid="privileges-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-48" data-testid="privileges-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="md:w-52" data-testid="privileges-category-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={featuredOnly}
            onChange={(e) => setFeaturedOnly(e.target.checked)}
            className="accent-primary"
            data-testid="privileges-featured-filter"
          />
          Homepage uniquement
        </label>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : offers.length === 0 ? (
          <div className="p-12 text-center">
            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Aucune offre pour ces critères.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto text-sm">
              <thead className="bg-secondary/60 text-muted-foreground text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Offre</th>
                  <th className="text-left px-3 py-2 font-medium whitespace-nowrap hidden lg:table-cell">Catégorie</th>
                  <th className="text-left px-3 py-2 font-medium whitespace-nowrap hidden xl:table-cell">Partenaire</th>
                  <th className="text-right px-3 py-2 font-medium whitespace-nowrap">Prix</th>
                  <th className="text-left px-3 py-2 font-medium whitespace-nowrap hidden xl:table-cell">Validité</th>
                  <th className="text-center px-3 py-2 font-medium whitespace-nowrap">Statut</th>
                  <th className="text-center px-3 py-2 font-medium whitespace-nowrap w-12">Actions</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o) => {
                  const meta = STATUS_META[o.status] || STATUS_META.draft;
                  const partner = o.partner_id ? partnersMap[o.partner_id] : null;
                  const end = o.end_date ? new Date(o.end_date).toLocaleDateString("fr-FR") : "—";
                  const start = o.start_date ? new Date(o.start_date).toLocaleDateString("fr-FR") : "—";
                  const isBusy = busyId === o.id;
                  const categoryLabel = CATEGORIES.find(c => c.value === o.category)?.label || o.category;
                  return (
                    <tr key={o.id} className="border-t border-border hover:bg-secondary/30" data-testid={`privilege-row-${o.id}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-3">
                          <Thumbnail mediaId={o.main_image_id} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-primary truncate max-w-[220px]" title={o.title}>{o.title}</span>
                              {o.featured_on_homepage && (
                                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" title="Homepage" />
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate max-w-[220px]" title={o.hook}>{o.hook}</p>
                            {/* Categorie + partenaire visibles sous title en mobile/tablet */}
                            <p className="text-xs text-muted-foreground lg:hidden mt-0.5">
                              {categoryLabel}{partner?.name ? ` • ${partner.name}` : ""}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap hidden lg:table-cell">
                        {categoryLabel}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap hidden xl:table-cell">
                        <span className="truncate max-w-[16ch] inline-block align-bottom" title={partner?.name || ""}>
                          {partner?.name || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {o.member_price != null ? (
                          <div>
                            <div className="font-semibold text-primary">{o.member_price.toFixed(2)} €</div>
                            {o.public_price != null && o.savings_percent > 0 && (
                              <div className="text-xs text-muted-foreground line-through">{o.public_price.toFixed(2)} €</div>
                            )}
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap hidden xl:table-cell">
                        {start} → {end}
                      </td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <Badge variant="outline" className={meta.cls}>{meta.label}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" disabled={isBusy} data-testid={`privilege-actions-${o.id}`}>
                              {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(o.id)} data-testid={`privilege-edit-${o.id}`}>
                              <Edit className="h-4 w-4 mr-2" /> Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => doAction(o.id, duplicateAdminPrivilege, "Offre dupliquée")} data-testid={`privilege-duplicate-${o.id}`}>
                              <Copy className="h-4 w-4 mr-2" /> Dupliquer
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {o.status !== "active" && (
                              <DropdownMenuItem onClick={() => doAction(o.id, activateAdminPrivilege, "Offre activée")} data-testid={`privilege-activate-${o.id}`}>
                                <Play className="h-4 w-4 mr-2" /> Activer
                              </DropdownMenuItem>
                            )}
                            {o.status === "active" && (
                              <DropdownMenuItem onClick={() => doAction(o.id, suspendAdminPrivilege, "Offre suspendue")} data-testid={`privilege-suspend-${o.id}`}>
                                <Pause className="h-4 w-4 mr-2" /> Suspendre
                              </DropdownMenuItem>
                            )}
                            {o.status !== "ended" && (
                              <DropdownMenuItem onClick={() => doAction(o.id, archiveAdminPrivilege, "Offre archivée")} data-testid={`privilege-archive-${o.id}`}>
                                <Archive className="h-4 w-4 mr-2" /> Archiver
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget(o)}
                              className="text-red-600 focus:text-red-700"
                              data-testid={`privilege-delete-${o.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Supprimer définitivement
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Editor */}
      {editorOpen && (
        <PrivilegeEditorDialog
          open={editorOpen}
          onOpenChange={handleEditorClose}
          privilegeId={editingId}
          partners={partners}
        />
      )}

      {/* Suppression confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <DialogContent data-testid="privilege-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer définitivement cette Escale Privilège&nbsp;?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Cette action est <strong>irréversible</strong>.
            </p>
            {deleteTarget && (
              <div className="bg-secondary rounded p-3 text-sm">
                <div className="font-medium text-primary">{deleteTarget.title}</div>
                {deleteTarget.hook && (
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{deleteTarget.hook}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={busyId === deleteTarget?.id}
              data-testid="privilege-delete-confirm-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Oui, supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
